import React, { useState, useEffect, useRef } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Input,
    Label,
    Alert,
    FormFeedback,
} from "reactstrap";
import { refundPaymentAuto } from "../../../api/requests/payment-requests";
import { toast } from "react-toastify";
import { isPaytrRefundClosed, PAYTR_REFUND_CLOSED_MESSAGE } from "../../../utils/paytrRefundWindow";

const formatPrice = (price) =>
    new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price || 0) + " ₺";

// Kalan iade edilebilir tutar. Backend `remainingRefundableAmount` döndürüyorsa o
// kullanılır; döndürmüyorsa (eski sürüm) ödeme tutarından bilinen iadeler düşülür.
const refundableOf = (payment) => {
    if (!payment) return 0;
    if (payment.remainingRefundableAmount != null) {
        return Math.max(parseFloat(payment.remainingRefundableAmount) || 0, 0);
    }
    const refunded = parseFloat(payment.totalRefunded ?? 0) || 0;
    return Math.max((parseFloat(payment.amount) || 0) - refunded, 0);
};

// Tutar alanı TR biçiminde yazılır/okunur: "3.456,66". Düz rakam (3456.66)
// kullanıcıya para gibi görünmüyordu; binlik ayracı olmadan 5 haneli tutarları
// gözle doğrulamak da zor.
const formatAmountInput = (value) => {
    if (value == null || value === "") return "";
    const n = typeof value === "string" ? parseAmountInput(value) : value;
    if (n === "" || Number.isNaN(n)) return "";
    return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
};

// "3.456,66" → 3456.66. Nokta binlik ayracı, virgül ondalık: biçimlendirme ile
// ayrıştırma aynı dili konuşmak zorunda, yoksa gönderilen tutar yazandan farklı olur.
function parseAmountInput(text) {
    if (text == null || text === "") return "";
    const cleaned = String(text).replace(/\./g, "").replace(",", ".");
    const n = parseFloat(cleaned);
    return Number.isNaN(n) ? "" : n;
}

// Kuruş bazında karşılaştırma — 0.1 + 0.2 tipi kayan nokta sapmalarına takılmamak için.
const toKurus = (value) => Math.round((parseFloat(value) || 0) * 100);

// Çift gönderim engeli için benzersiz anahtar (secure context'te crypto.randomUUID; değilse fallback).
const newIdempotencyKey = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * PaymentRefundModal - sanal POS iadesi / iptali
 *
 * Props
 * ─────────────────────────────────────────────────────────
 * @param {boolean}  isOpen     - Modal açık mı?
 * @param {Function} toggle     - Modal'ı kapat/aç
 * @param {Object}   payment    - İade edilecek ödeme (id, merchantOid, amount, status)
 * @param {Function} onConfirm  - Başarılı iade sonrası callback (listeyi tazeler)
 *
 * Not: Tutar alanı kalan iade edilebilir tutarla dolu açılır; `refundAmountFixed` değilse kısmi
 * iade için üzerine yazılır. Onay kutusu yalnızca UX teyididir — nihai kontrol backend'de yapılır.
 */
const PaymentRefundModal = ({ isOpen, toggle, payment, onConfirm }) => {
    const [amount, setAmount] = useState("");
    const [confirmed, setConfirmed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState("");
    const [now, setNow] = useState(() => new Date());

    // Pencere açık kaldığı sürece saat tazelenir; 11:30 geçince düğme kendiliğinden açılır.
    useEffect(() => {
        if (!isOpen) return undefined;
        setNow(new Date());
        const t = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(t);
    }, [isOpen]);
    const idempotencyKeyRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            // Alan boş açılmaz: varsayılan kalan iade edilebilir tutarın tamamı.
            const remaining = refundableOf(payment);
            setAmount(remaining > 0 ? formatAmountInput(remaining) : "");
            setConfirmed(false);
            setErrors({});
            // Her açılışta yeni anahtar → aynı açılış içindeki çift-tık/çift-fire aynı anahtarla 409'a takılır.
            idempotencyKeyRef.current = newIdempotencyKey();
        }
    }, [isOpen, payment]);

    if (!payment) return null;

    const paytrClosed = payment.virtualPos === "PAYTR" && isPaytrRefundClosed(now);
    const isIsyerimPos = payment.virtualPos === "ISYERIM_POS";
    const isPartiallyRefunded = payment.status === "PARTIALLY_REFUNDED";
    const isCancel = payment.refundOperation === "VOID";
    const amountFixed = !!payment.refundAmountFixed;
    const operationWord = isCancel ? "iptal" : "iade";
    const VPOS_LABELS = { PAYTR: "PayTR", GARANTI: "Garanti BBVA", ISYERIM_POS: "İşyerimPOS" };
    const providerLabel = payment.virtualPosLabel || VPOS_LABELS[payment.virtualPos] || "sanal POS";

    const totalRefunded = parseFloat(payment.totalRefunded ?? 0) || 0;
    const remaining = refundableOf(payment);

    const validate = () => {
        const newErrors = {};
        const val = parseAmountInput(amount);
        if (amount === "" || val === "" || isNaN(val) || val <= 0) {
            newErrors.amount = "İade tutarı 0'dan büyük olmalıdır";
        } else if (toKurus(val) > toKurus(remaining)) {
            newErrors.amount = `İade tutarı kalan iade edilebilir tutarı (${formatPrice(remaining)}) aşamaz`;
        }
        return newErrors;
    };

    // Tutar değişince onay sıfırlanır: onay belirli bir tutara veriliyor, tutar
    // değiştiğinde eski onay artık o tutarı temsil etmez.
    const handleAmountChange = (e) => {
        // Yazarken biçimlendirmiyoruz: her tuşta imleç sona atlar ve kuruş
        // girmek imkânsızlaşır. Yalnız geçersiz karakterler ayıklanır.
        setAmount(e.target.value.replace(/[^0-9.,]/g, ""));
        setConfirmed(false);
        if (errors.amount) setErrors((prev) => ({ ...prev, amount: undefined }));
        setSubmitError("");
    };

    // Alandan çıkınca tutar "3.456,66" biçimine oturur.
    const handleAmountBlur = () => {
        const parsed = parseAmountInput(amount);
        if (parsed !== "" && parsed > 0) setAmount(formatAmountInput(parsed));
    };

    const handleConfirm = async () => {
        const validationErrors = validate();
        // Onay kutusu tutar her değiştiğinde sıfırlanıyor. Butonu pasifleştirmek
        // kullanıcıya "tıklıyorum bir şey olmuyor" yaşatıyordu; artık buton aktif
        // ve eksik olan şey söyleniyor.
        if (!confirmed) {
            validationErrors.confirm = `Devam etmek için ${operationWord} işlemini onaylayın.`;
        }
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setErrors({});
        setSubmitError("");
        try {
            setSaving(true);
            const refundValue = parseAmountInput(amount);
            await refundPaymentAuto(payment, refundValue, idempotencyKeyRef.current);
            toast.success(isCancel ? "İptal başarıyla gerçekleştirildi" : "İade başarıyla gerçekleştirildi",
                { containerId: "global-toaster" });
            toggle();
            if (onConfirm) onConfirm();
        } catch (err) {
            setSubmitError(err.message || `${isCancel ? "İptal" : "İade"} işlemi başarısız oldu`);
            // Başarısız denemeden sonra yeni anahtar → düzeltip tekrar denenebilsin (aksi halde 409'a takılır).
            idempotencyKeyRef.current = newIdempotencyKey();
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!saving) {
            setAmount("");
            setConfirmed(false);
            setErrors({});
            setSubmitError("");
            toggle();
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered>
            <ModalHeader toggle={handleClose} className="border-bottom">
                <div className="d-flex align-items-center gap-2 text-warning">
                    <i className="bx bx-undo fs-4"></i>
                    {isCancel ? "İşlem İptali" : "Tutar İadesi"}
                </div>
            </ModalHeader>

            <ModalBody className="py-4">
                <Alert color="warning" className="mb-3 d-flex align-items-start gap-2">
                    <i className="bx bx-info-circle mt-1 flex-shrink-0" />
                    {amountFixed ? (
                        <span>
                            Bu işlem {providerLabel} üzerinden gerçek bir para {isCancel ? "iptali" : "iadesi"} başlatır
                            ve geri alınamaz. Ödeme bugün alındığı için <strong>tutarın tamamı</strong> geri alınır;
                            kısmi iade yarından itibaren yapılabilir.
                        </span>
                    ) : (
                        <span>
                            Bu işlem {providerLabel} üzerinden gerçek bir para iadesi başlatır ve geri alınamaz.
                            Tutar alanı <strong>kalan iade edilebilir tutarla</strong> dolu gelir; kısmi iade için
                            üzerine yazabilirsiniz.
                        </span>
                    )}
                </Alert>

                <div className="border rounded p-3 bg-light mb-3">
                    <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Sipariş No:</span>
                        <span className="fw-semibold">{payment.merchantOid || "-"}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Orijinal Tutar:</span>
                        <span className="fw-semibold text-success">{formatPrice(payment.amount)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Toplam İade:</span>
                        <span className="fw-semibold">{formatPrice(totalRefunded)}</span>
                    </div>
                    <div className="d-flex justify-content-between border-top pt-2">
                        <span className="text-muted">İade Edilebilir:</span>
                        <span className="fw-bold">{formatPrice(remaining)}</span>
                    </div>
                </div>

                {paytrClosed && (
                    <Alert color="danger" className="mb-3 d-flex align-items-start gap-2">
                        <i className="bx bx-time-five mt-1 flex-shrink-0" />
                        <span>{PAYTR_REFUND_CLOSED_MESSAGE}</span>
                    </Alert>
                )}

                {isIsyerimPos && (
                    <Alert color="warning" className="mb-3 d-flex align-items-start gap-2">
                        <i className="bx bx-lock-alt mt-1 flex-shrink-0" />
                        <span>
                            İşyerimPOS'ta iptal ve iade için firmanıza özel yetki tanımlanması gerekir. Bu yetki
                            İşyerimPOS tarafından açılmadıysa işlem hata verir; yetki için İşyerimPOS ile iletişime geçin.
                        </span>
                    </Alert>
                )}

                {isPartiallyRefunded && !amountFixed && (
                    <Alert color="info" className="mb-3 py-2 d-flex align-items-start gap-2">
                        <i className="bx bx-history mt-1 flex-shrink-0" />
                        <span>Bu ödemeye daha önce kısmi iade yapılmış. Yeni tutar kalan bakiyeden düşülür.</span>
                    </Alert>
                )}

                <div>
                    <Label className="text-muted mb-1 fw-semibold" style={{ fontSize: ".9rem" }}>
                        {isCancel ? "İptal Tutarı" : "İade Tutarı"}
                    </Label>
                    <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="0,00"
                        value={amount}
                        onChange={handleAmountChange}
                        onBlur={handleAmountBlur}
                        invalid={!!errors.amount}
                        disabled={saving || paytrClosed || amountFixed}
                    />
                    {errors.amount && <FormFeedback>{errors.amount}</FormFeedback>}
                </div>

                {/* Onay kutusu yalnızca UX teyididir; iadeyi asıl doğrulayan backend. */}
                <div className="form-check mt-3">
                    <Input
                        type="checkbox"
                        className="form-check-input"
                        id="refund-confirm"
                        checked={confirmed}
                        onChange={(e) => {
                            setConfirmed(e.target.checked);
                            if (e.target.checked && errors.confirm) {
                                setErrors((prev) => ({ ...prev, confirm: undefined }));
                            }
                        }}
                        disabled={saving || paytrClosed}
                    />
                    <Label className="form-check-label" htmlFor="refund-confirm">
                        {formatPrice(parseAmountInput(amount) || 0)} tutarında {operationWord} işlemini onaylıyorum.
                    </Label>
                </div>
                {errors.confirm && (
                    <div className="text-danger small mt-2">{errors.confirm}</div>
                )}
                {submitError && (
                    <Alert color="danger" className="mt-3 mb-0 d-flex align-items-start gap-2">
                        <i className="bx bx-error-circle mt-1 flex-shrink-0" />
                        <span>{submitError}</span>
                    </Alert>
                )}
            </ModalBody>

            <ModalFooter className="border-top">
                {/* Buton geometrisi diğer ana aksiyonlarla aynı: btn-rounded + px-4.
                    "Vazgeç" bilerek light — custom.css `.btn-secondary`yi lacivert
                    dolguya çevirdiği için ikincil aksiyon birincil gibi görünüyordu.
                    Onay butonu danger: geri alınamaz finansal işlem. */}
                <div className="d-flex justify-content-end gap-2 w-100">
                    <Button color="light" onClick={handleClose} disabled={saving} className="btn-rounded px-4">
                        Vazgeç
                    </Button>
                    <Button
                        color="danger"
                        onClick={handleConfirm}
                        disabled={saving || paytrClosed}
                        className="btn-rounded px-4 d-flex align-items-center gap-2"
                        style={{ minWidth: "150px" }}
                    >
                        {saving ? (
                            <>
                                <Spinner size="sm" />
                                İşleniyor...
                            </>
                        ) : (
                            <>
                                <i className="bx bx-undo"></i>
                                {isCancel ? "İptali Onayla" : "İadeyi Onayla"}
                            </>
                        )}
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default PaymentRefundModal;
